import React from "react";
import {
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enGB, th } from "date-fns/locale";

const LOCALES = { en: enGB, th };

// Thai dates use the Buddhist Era, 543 years ahead of the Gregorian year
const BUDDHIST_ERA_OFFSET = 543;

// Same as AdapterDateFns, but shows and reads years in the Buddhist Era. The picked
// value stays a normal Date, only the displayed year changes
class AdapterDateFnsBuddhist extends AdapterDateFns {
  constructor(...args) {
    super(...args);
    const baseFormatByString = this.formatByString;
    const baseParse = this.parse;

    // Year tokens become the Buddhist year as quoted literal text, quoted parts are left as they are
    this.formatByString = (date, formatString) => {
      const buddhistYear = `'${this.getYear(date) + BUDDHIST_ERA_OFFSET}'`;
      return baseFormatByString(
        date,
        this.expandFormat(formatString).replace(/'(?:''|[^'])*'?|y+/g, (token) =>
          token.startsWith("'") ? token : buddhistYear
        )
      );
    };

    this.parse = (value, formatString) => {
      const date = baseParse(value, formatString);
      return date && this.isValid(date)
        ? this.addYears(date, -BUDDHIST_ERA_OFFSET)
        : date;
    };
  }
}

const ADAPTERS = { en: AdapterDateFns, th: AdapterDateFnsBuddhist };

const BRAND_BLUE = "#1560D4";

const calendarSx = {
  "& .MuiPickersDay-root:hover": { bgcolor: "#E8F0FA" },
  "& .MuiPickersDay-root.Mui-selected, & .PrivatePickersYear-yearButton.Mui-selected":
    {
      bgcolor: BRAND_BLUE,
      color: "#fff",
      "&:hover, &:focus": { bgcolor: BRAND_BLUE },
    },
  "& .MuiPickersDay-today:not(.Mui-selected)": {
    borderColor: BRAND_BLUE,
    color: BRAND_BLUE,
  },
};

const toggleButtonSx = {
  px: "12px",
  py: "2px",
  "&.Mui-selected, &.Mui-selected:hover": {
    bgcolor: BRAND_BLUE,
    color: "#fff",
  },
};

// Rendered as the calendar's action bar, so the toggle sits inside the popup
const CalendarLanguageToggle = ({ language, onLanguageChange }) => (
  <Stack direction="row" justifyContent="flex-end" sx={{ px: "16px", pb: "12px" }}>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={language}
      onChange={(_, value) => value && onLanguageChange(value)}
    >
      <ToggleButton value="en" sx={toggleButtonSx}>
        EN
      </ToggleButton>
      <ToggleButton value="th" sx={toggleButtonSx}>
        TH
      </ToggleButton>
    </ToggleButtonGroup>
  </Stack>
);

export const FtiDatePicker = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  sx,
}) => {
  const [language, setLanguage] = React.useState("en");

  return (
    <LocalizationProvider
      dateAdapter={ADAPTERS[language]}
      adapterLocale={LOCALES[language]}
    >
      <DesktopDatePicker
        label={label}
        value={value}
        onChange={onChange}
        inputFormat="dd/MM/yyyy"
        minDate={minDate}
        maxDate={maxDate}
        disableFuture
        // The default keeps only the first letter, which repeats in Thai (อา. / อ.)
        dayOfWeekFormatter={(day) => day}
        PaperProps={{ sx: calendarSx }}
        components={{ ActionBar: CalendarLanguageToggle }}
        componentsProps={{
          actionBar: { language, onLanguageChange: setLanguage },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onKeyDown={(e) => {
              e.preventDefault();
            }}
            sx={sx}
          />
        )}
      />
    </LocalizationProvider>
  );
};
